import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("rwm_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- Resilience: auto-retry silently on transient upstream errors (502/503/504,
// network drop) so brief cloud-container restarts don't surface as broken UI.
// Retries 1-2 are completely silent so short blips stay invisible. Only if the
// server is still unreachable at retry 3+ do we show a subtle toast.
const RETRY_STATUSES = new Set([502, 503, 504]);
const MAX_RETRIES = 4;
const BASE_DELAY_MS = 800;
const TOAST_AFTER_RETRY = 3; // only show the toast if we've been retrying 3+ times
let toastShownAt = 0;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

api.interceptors.response.use(
  (response) => {
    // If a "reconnecting" toast is currently showing, tell the user we're back
    if (toastShownAt && Date.now() - toastShownAt < 20000) {
      toast.success("Back online", { id: "conn-status", duration: 2000 });
      toastShownAt = 0;
    }
    return response;
  },
  async (error) => {
    const config = error.config || {};
    const status = error.response?.status;
    const isNetworkError = !error.response;
    const shouldRetry = isNetworkError || RETRY_STATUSES.has(status);

    if (shouldRetry && config) {
      config.__retryCount = (config.__retryCount || 0) + 1;
      if (config.__retryCount <= MAX_RETRIES) {
        // 800ms → 1.6s → 3.2s → 6.4s = ~12s of coverage, silent for first 3
        const delay = BASE_DELAY_MS * Math.pow(2, config.__retryCount - 1);

        // Only surface a toast if we've been retrying for 3+ attempts
        // (meaning the outage is longer than ~4s of retries)
        if (config.__retryCount === TOAST_AFTER_RETRY && Date.now() - toastShownAt > 8000) {
          toast.loading("Reconnecting…", { id: "conn-status", duration: 6000 });
          toastShownAt = Date.now();
        }

        await sleep(delay);
        return api.request(config);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
